// helper for logging on sentry and console without repeating same messages all the time
// too many requests on sentry will cause a http status "429 Too many requests"
import * as Sentry from "@sentry/react";

type SeverityLevel = "log" | "error" | "info" | "debug" | "warning";

const messagesLog: string[] = [];

function messageAlreadyLogged(message: string): boolean {
  return messagesLog.includes(message);
}

export function logConsoleOnce(message: string, severity: SeverityLevel = "log"): void {
  // prevent when already logged this msg
  if (messageAlreadyLogged(message)) {
    return;
  }
  messagesLog.push(message);
  logBrowser(message, severity);
}

export function devLogInfo(message?: any, ...optionalParams: any[]): void {
  if (process.env.REACT_APP_ENABLE_DEV_LOG !== "true") {
    return;
  }

  console.info(message, ...optionalParams);
}

export function devLogDebugOnce(message?: any, ...optionalParams: any[]): void {
  if (process.env.REACT_APP_ENABLE_DEV_LOG !== "true") {
    return;
  }

  // prevent when already logged this msg
  if (messageAlreadyLogged(message + JSON.stringify(optionalParams))) {
    return;
  }

  messagesLog.push(message + JSON.stringify(optionalParams));

  console.debug(message, ...optionalParams);
}

export function devLogInfoOnce(message?: any, ...optionalParams: any[]): void {
  if (process.env.REACT_APP_ENABLE_DEV_LOG !== "true") {
    return;
  }

  // prevent when already logged this msg
  if (messageAlreadyLogged(message + JSON.stringify(optionalParams))) {
    return;
  }

  messagesLog.push(message + JSON.stringify(optionalParams));

  console.info(message, ...optionalParams);
}

function logBrowser(message: string, severity: SeverityLevel = "log"): void {
  if (severity === "log") {
    console.log(message);
  }

  if (severity === "error") {
    console.error(message);
  }

  if (severity === "info") {
    console.info(message);
  }

  if (severity === "debug") {
    console.debug(message);
  }

  if (severity === "warning") {
    console.warn(message);
  }
}

// Start of Helpers for Sentry capture logging
type Data = Record<string, unknown> & { exception?: unknown };

// too many requests on sentry will cause a http status "429 Too many requests"
// the limit is chosen arbitrarily
const MESSAGE_LOG_LIMIT = 1;
const logMessageMap: Map<string, number> = new Map();

const updateLogMessageMapFor = (message: string) => {
  const mapEntry = logMessageMap.get(message) ?? 0;
  if (mapEntry > MESSAGE_LOG_LIMIT) {
    console.log(`Suppressing log message: ${message}`);
    return false;
  }
  logMessageMap.set(message, mapEntry + 1);
  return true;
};

const setExtrasIfAny = (scope: Sentry.Scope, ...extras: (Data | undefined)[]) => {
  const flatExtras = extras.reduce((acc: Data, extra = {}) => ({ ...acc, ...extra }), {});

  if (Object.keys(flatExtras).length > 0) {
    scope.setExtras(flatExtras);
  }
};

const captureWithException = (level: SeverityLevel, message: string, data: Data | undefined) => {
  const shallCapture = updateLogMessageMapFor(message);
  if (!shallCapture) {
    return;
  }
  if (data?.exception && data?.exception instanceof Error) {
    const { exception, ...properties } = data;
    Sentry.withScope(scope => {
      setExtrasIfAny(scope, { ...properties, message });
      Sentry.captureException(exception, {
        level,
      });
    });
  } else {
    Sentry.withScope(scope => {
      setExtrasIfAny(scope, data);
      Sentry.captureMessage(message, level);
    });
  }
};

export const logger = {
  error: (message: string, data?: Data) => {
    console.error(message, data);
    captureWithException("error", message, data);
  },
  warn: (message: string, data?: Data) => {
    console.warn(message, data);
    captureWithException("warning", message, data);
  },
  debug: (message: string, data?: Data) => {
    console.info(message, data);
    captureWithException("debug", message, data);
  },
};
